import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import OrganizationProfileModal from './OrganizationProfileModal';

interface OrganizationProfile {
  id: string;
  organizationId: string;
  companyName: string;
  companyMotto?: string;
  companyWebsite?: string;
  companyPhone?: string;
  companyABN?: string;
  companyACN?: string;
  logoFileName?: string;
  logoUrl?: string;
  otherInfo?: string;
  createdAt: string;
  updatedAt: string;
}

interface OrganizationHeaderProps {
  organizationId: string;
  className?: string;
  showName?: boolean;
  showDropdownIcon?: boolean;
  onClick?: () => void;
}

export default function OrganizationHeader({ 
  organizationId, 
  className = "",
  showDropdownIcon = false,
  onClick
}: OrganizationHeaderProps) {
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Fetch organization profile
  const { data: profile, error, isLoading } = useQuery<OrganizationProfile>({
    queryKey: ['organization-profile', organizationId],
    queryFn: () => api.get(`/organizations/profile/${organizationId}`).then(res => res.data.data),
    enabled: !!organizationId,
    retry: 1, // Only retry once to avoid infinite loading
  });

  // Generate default logo (blue box with first letter)
  const generateDefaultLogo = (name: string) => (
    <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center text-white text-sm font-bold">
      {name.charAt(0).toUpperCase()}
    </div>
  );

  const handleHeaderClick = () => {
    if (onClick) {
      onClick();
    } else {
      setIsProfileModalOpen(true);
    }
  };

  // Handle loading and error states
  if (isLoading) {
    return (
      <div className={`flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity ${className}`}>
        <div className="w-8 h-8 bg-gray-300 rounded animate-pulse"></div>
        <span className="text-lg font-semibold text-gray-300 animate-pulse">Loading...</span>
        {showDropdownIcon && <ChevronDown className="h-4 w-4 text-gray-400" />}
      </div>
    );
  }

  // If there's an error or no profile, show fallback with organization name
  if (error || !profile) {
    const fallbackName = organizationId === 'nexus' ? 'Nexus' : 
                        organizationId === '550e8400-e29b-41d4-a716-446655440000' ? 'TechFlow Solutions' :
                        'Organization';
    
    return (
      <>
        <div 
          className={`flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity ${className}`}
          onClick={handleHeaderClick}
          title="Click to view organization profile"
        >
          {generateDefaultLogo(fallbackName)}
          <span className="text-lg font-semibold text-foreground">{fallbackName}</span>
          {showDropdownIcon && <ChevronDown className="h-4 w-4 text-gray-500" />}
        </div>

        {/* Organization Profile Modal */}
        <OrganizationProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          organizationId={organizationId}
        />
      </>
    );
  }

  return (
    <>
      <div 
        className={`flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity ${className}`}
        onClick={handleHeaderClick}
        title="Click to view organization profile"
      >
        {/* Organization Logo */}
        {profile.logoUrl ? (
          <img 
            src={profile.logoUrl} 
            alt={`${profile.companyName} Logo`} 
            className="w-8 h-8 rounded object-cover"
          />
        ) : (
          generateDefaultLogo(profile.companyName)
        )}
        
        {/* Organization Name */}
        <span className="text-lg font-semibold text-foreground">
          {profile.companyName}
        </span>
        {showDropdownIcon && <ChevronDown className="h-4 w-4 text-gray-500" />}
      </div>

      {/* Organization Profile Modal */}
      <OrganizationProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        organizationId={organizationId}
      />
    </>
  );
}
